import React from 'react';
import { useState, useEffect } from 'react';
import { Box, Skeleton, Stack, Typography } from '@compass/core';
import { Alert } from '@mui/material';
import { Select } from 'components';
import { SchoolEnrollmentDataProvider, useSchoolEnrollmentData } from '../contexts/SchoolEnrollmentDataContext';
import { SchoolEnrollmentChart } from './SchoolEnrollmentChart';
import { DashboardCard } from '../components/DashboardCard';

const SchoolEnrollmentCardContent: React.FC = () => {
    const { data, loading, error } = useSchoolEnrollmentData();

    if (loading) {
        return (
            <Stack spacing="md" sx={{ p: 2 }}>
                <Skeleton variant="rounded" height={40} />
                <Skeleton variant="rounded" height={300} />
            </Stack>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SchoolEnrollmentChart data={data} />
        </Box>
    );
};

interface YearOption {
    value: number;
    label: string;
}

export const SchoolEnrollmentCard: React.FC = () => {
    const [selectedYear, setSelectedYear] = useState<YearOption | null>(null);
    const [availableYears, setAvailableYears] = useState<YearOption[]>([]);

    // Initialize years on mount
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        
        // Generate year range: current year � 5 years
        const years: YearOption[] = [];
        for (let i = -5; i <= 0; i++) { // Past 5 years + current only
            const year = currentYear + i;
            years.push({
                value: year,
                label: `${year}-${(year + 1).toString().slice(2)}`
            });
        }
        
        // Default to current year
        const defaultYear = years.find(y => y.value === currentYear);
        
        setAvailableYears(years);
        setSelectedYear(defaultYear || years[years.length - 1]);
    }, []);

    const handleYearChange = (event: any, value: YearOption | null) => {
        if (value) {
            setSelectedYear(value);
        }
    };

    // Don't render until we have a selected year
    if (selectedYear === null) {
        return (
            <DashboardCard title="Schools with Highest Enrollments">
                <Box sx={{ p: 2 }}>
                    <Skeleton variant="rounded" height={300} />
                </Box>
            </DashboardCard>
        );
    }

    return (
        <DashboardCard title="Schools with Highest Enrollments">
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* School Year Selector Dropdown */}
                <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="medium" sx={{ mr: 1 }}>
                        School Year
                    </Typography>
                    <Box sx={{ width: 225 }}>
                        <Select
                            value={selectedYear}
                            options={availableYears}
                            getOptionLabel={(option: YearOption) => option.label}
                            onChange={handleYearChange}
                            size="small"
                        />
                    </Box>
                </Stack>

                {/* Card Content with Context Provider */}
                <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <SchoolEnrollmentDataProvider schoolYear={selectedYear.value}>
                        <SchoolEnrollmentCardContent />
                    </SchoolEnrollmentDataProvider>
                </Box>
            </Box>
        </DashboardCard>
    );
};